import Card from '@/components/UI/Card';
import { formatBooleanFlag, formatEnumLabel, formatMetric } from './helpers';
import MetricPill from './MetricPill';
import type { ObvAuditResponse } from './types';

interface FocusedDailySnapshotCardProps {
    focusedDailySnapshot: ObvAuditResponse['focusedDailySnapshot'];
}

const FocusedDailySnapshotCard = ({ focusedDailySnapshot }: FocusedDailySnapshotCardProps) => (
    <Card responsivePadding bodyClassName="space-y-4" title="Focused Daily Screener Snapshot">
        {focusedDailySnapshot ? (
            <>
                <div className="text-sm text-zinc-400">Daily materialized snapshot for {focusedDailySnapshot.date}</div>

                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                    <MetricPill label="Directional Bias" value={formatEnumLabel(focusedDailySnapshot.technicals.obvDirectionalBias)} />
                    <MetricPill
                        label="Signal Relationship"
                        value={formatEnumLabel(focusedDailySnapshot.technicals.obvSignalRelationship)}
                    />
                    <MetricPill label="Structure Trend" value={formatEnumLabel(focusedDailySnapshot.technicals.obvStructureTrend)} />
                    <MetricPill label="Breakout State" value={formatEnumLabel(focusedDailySnapshot.technicals.obvBreakoutState50)} />
                    <MetricPill label="Flow Balance 20" value={formatMetric(focusedDailySnapshot.technicals.obvFlowBalance20, 3)} />
                    <MetricPill label="Flow Balance 50" value={formatMetric(focusedDailySnapshot.technicals.obvFlowBalance50, 3)} />
                    <MetricPill label="Flow Balance 65" value={formatMetric(focusedDailySnapshot.technicals.obvFlowBalance65, 3)} />
                    <MetricPill label="Signal Distance 20" value={formatMetric(focusedDailySnapshot.technicals.obvSignalDistance20, 3)} />
                    <MetricPill
                        label="Local Percentile 100"
                        value={formatMetric(focusedDailySnapshot.technicals.obvLocalPercentile100, 3)}
                    />
                    <MetricPill
                        label="Accumulating Range"
                        value={formatBooleanFlag(focusedDailySnapshot.technicals.obvIsAccumulatingRange)}
                    />
                    <MetricPill
                        label="Distributing Range"
                        value={formatBooleanFlag(focusedDailySnapshot.technicals.obvIsDistributingRange)}
                    />
                    <MetricPill
                        label="Bullish Divergence Active"
                        value={formatBooleanFlag(focusedDailySnapshot.technicals.obvBullishDivergenceActive)}
                    />
                    <MetricPill
                        label="Bearish Divergence Active"
                        value={formatBooleanFlag(focusedDailySnapshot.technicals.obvBearishDivergenceActive)}
                    />
                    <MetricPill
                        label="Bars Since Bullish Divergence"
                        value={
                            focusedDailySnapshot.technicals.obvBarsSinceBullishDivergence === null
                                ? 'n/a'
                                : String(focusedDailySnapshot.technicals.obvBarsSinceBullishDivergence)
                        }
                    />
                    <MetricPill
                        label="Bars Since Bearish Divergence"
                        value={
                            focusedDailySnapshot.technicals.obvBarsSinceBearishDivergence === null
                                ? 'n/a'
                                : String(focusedDailySnapshot.technicals.obvBarsSinceBearishDivergence)
                        }
                    />
                </div>

                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                    <MetricPill label="Flow Balance 20 p90" value={formatMetric(focusedDailySnapshot.quantiles.flowBalance20?.p90, 3)} />
                    <MetricPill label="Flow Balance 50 p90" value={formatMetric(focusedDailySnapshot.quantiles.flowBalance50?.p90, 3)} />
                    <MetricPill label="Flow Balance 65 p90" value={formatMetric(focusedDailySnapshot.quantiles.flowBalance65?.p90, 3)} />
                    <MetricPill
                        label="Signal Distance 20 p90"
                        value={formatMetric(focusedDailySnapshot.quantiles.signalDistance20?.p90, 3)}
                    />
                </div>
            </>
        ) : (
            <div className="text-sm text-zinc-400">
                No daily screener snapshot was found for the focused date. That usually means the focused date is outside the daily
                materialized coverage or the base daily row is missing.
            </div>
        )}
    </Card>
);

export default FocusedDailySnapshotCard;
